import { getBackendUrl } from '@/lib/env';

/**
 * Resolve a public-disk relative path (or absolute URL) to a usable <img> src.
 * Preserves remote URLs (e.g. production/staging or external CDNs) so live assets
 * are never broken when developing locally.
 */
export function resolveStorageUrl(path?: string | null): string | null {
  if (!path?.trim()) return null;
  const raw = path.trim();
  if (raw.startsWith('blob:')) return raw;

  const backend = getBackendUrl().replace(/\/$/, '');
  const isBrowser = typeof window !== 'undefined';
  const isBrowserLocal =
    isBrowser &&
    Boolean(
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '[::1]'
    );
  const runtimeBackend = typeof window !== 'undefined' ? ((window as any).__ENTHIOS_RUNTIME__?.backendUrl || (window as any).RUNTIME_CONFIG?.backendUrl) : null;
  const isTargetRemote = Boolean(runtimeBackend && !(runtimeBackend as string).includes('localhost') && !(runtimeBackend as string).includes('127.0.0.1')) || Boolean(backend && !backend.includes('localhost') && !backend.includes('127.0.0.1')) || (isBrowser && !isBrowserLocal);

  // Handle accidental protocol-less localhost URLs like localhost:37857/9754995.png
  const isBareLoopback = /^(127\.0\.0\.1|localhost)(:\d+)?(\/.*)?$/i.test(raw);
  if (isBareLoopback) {
    const storageIdx = raw.indexOf('/storage/');
    if (storageIdx >= 0 && backend) {
      const storagePath = raw.slice(storageIdx + '/storage/'.length);
      return `${backend}/storage/${storagePath.replace(/^\/+/, '')}`;
    }
    if (isTargetRemote) {
      return null;
    }
    return `http://${raw}`;
  }

  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    try {
      const parsed = new URL(raw);

      const isLoopbackHost =
        parsed.hostname === 'localhost' ||
        parsed.hostname === '127.0.0.1' ||
        parsed.hostname === '[::1]';

      if (isLoopbackHost) {
        const storageIdx = parsed.pathname.indexOf('/storage/');
        if (storageIdx >= 0 && backend) {
          const storagePath = parsed.pathname.slice(storageIdx + '/storage/'.length);
          return `${backend}/storage/${storagePath.replace(/^\/+/, '')}`;
        }
        if (isTargetRemote) {
          // Never request loopback/dev endpoints from a staging or production client
          return null;
        }
        return raw;
      }

      // If it's already an absolute URL to a remote EnthiosHub server or external CDN,
      // do NOT rewrite to localhost:8000 where the file doesn't exist.
      const isRemoteHost =
        parsed.hostname === 'admin.enthioshub.com' ||
        parsed.hostname === 'api.enthioshub.com' ||
        parsed.hostname === 'enthioshub.com' ||
        parsed.hostname.endsWith('.enthioshub.com') ||
        parsed.hostname.includes('unsplash.com');

      if (isRemoteHost) {
        return raw;
      }

      const storageIdx = parsed.pathname.indexOf('/storage/');
      if (storageIdx >= 0 && backend) {
        const storagePath = parsed.pathname.slice(storageIdx + '/storage/'.length);
        return `${backend}/storage/${storagePath.replace(/^\/+/, '')}`;
      }
      return raw;
    } catch {
      return raw;
    }
  }

  let cleaned = raw.replace(/^\/+/, '').replace(/^storage\//, '');

  // If bare filename without a directory prefix (e.g. "01B958ypZl6ye5XQznaAyPaTDtafqp4jdFEXsRZw.jpg"),
  // default to events/covers where wizard cover uploads are stored.
  if (!cleaned.includes('/')) {
    cleaned = `events/covers/${cleaned}`;
  }

  if (!backend) {
    return `/storage/${cleaned}`;
  }
  return `${backend}/storage/${cleaned}`;
}
